import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Paper,
  Divider,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  CloudUpload,
  List as ListIcon,
  Analytics,
  FindInPage,
  Settings,
  Refresh,
  ArrowUpward,
  ArrowDownward,
  Remove as UnchangedIcon,
  RateReview,
  Schedule,
  Restaurant,
  ShoppingBag,
  TheaterComedy,
  EmojiEvents,
} from '@mui/icons-material';
import { dashboardApi, analyticsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate, getMonthName } from '../utils/helpers';
import LoadingButton from '../components/LoadingButton';
import BudgetWidget from '../components/BudgetWidget';
import SpendingHabitsTable from '../components/SpendingHabitsTable';
import UpcomingWidget from '../components/UpcomingWidget';
import ForwardingSetupCard from '../components/ForwardingSetupCard';
import BillsWidget from '../components/BillsWidget';
import MoneyMovedCards from '../components/MoneyMovedCards';
import InsightsCards from '../components/InsightsCards';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dateRanges, setDateRanges] = useState({ years: [], months: [] });
  const [accountIds, setAccountIds] = useState([]);
  const [budgetStatus, setBudgetStatus] = useState([]);
  const [spendingInsights, setSpendingInsights] = useState([]);
  const [pendingReview, setPendingReview] = useState({ totalPending: 0, pendingJobs: 0, recentItems: [] });
  const [spendingHabits, setSpendingHabits] = useState(null);
  const [historicalBudgets, setHistoricalBudgets] = useState(null);
  const [habitsHistory, setHabitsHistory] = useState(null);
  const [defaultsSet, setDefaultsSet] = useState(false);
  
  // Check if initial state has URL params (compute only once)
  const [hasInitialParams] = useState(() => {
    return !!(searchParams.get('year') || searchParams.get('month') || 
             searchParams.get('accountType') || searchParams.get('accountId'));
  });

  // Filter states
  const [selectedYear, setSelectedYear] = useState(searchParams.get('year') || '');
  const [selectedMonth, setSelectedMonth] = useState(searchParams.get('month') || '');
  const [selectedAccountType, setSelectedAccountType] = useState(searchParams.get('accountType') || '');
  const [selectedAccountId, setSelectedAccountId] = useState(searchParams.get('accountId') || '');

  // Cleanup: Clear Dashboard params when navigating away
  useEffect(() => {
    return () => {
      const url = new URL(window.location);
      url.search = '';
      window.history.replaceState({}, '', url);
    };
  }, []);

  // Set default to current month/year on initial load
  useEffect(() => {
    if (hasInitialParams && !defaultsSet) {
      setDefaultsSet(true);
      return;
    }
    
    if (!defaultsSet && dateRanges.years.length > 0 && !selectedYear) {
      if (!hasInitialParams) {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonthNum = currentDate.getMonth() + 1;
        const currentMonthStr = currentMonthNum.toString();
        
        if (dateRanges.years.includes(currentYear)) {
          // Always set current year and month for better UX
          setSelectedYear(currentYear.toString());
          setSelectedMonth(currentMonthStr);
        } else {
          // Fall back to most recent year if current year has no data
          setSelectedYear(dateRanges.years[0].toString());
        }
        
        setDefaultsSet(true);
      }
    }
  }, [dateRanges, defaultsSet, selectedYear, selectedMonth, hasInitialParams]);

  useEffect(() => {
    if (defaultsSet || hasInitialParams) {
      loadDashboardData();
    } else {
      loadInitialDateRanges();
    }
  }, [selectedYear, selectedMonth, selectedAccountType, selectedAccountId, defaultsSet, hasInitialParams]);

  const loadInitialDateRanges = async () => {
    try {
      const response = await analyticsApi.getDateRanges();
      setDateRanges(response.data);
    } catch (error) {
      console.error('Error loading date ranges:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (selectedYear) params.year = selectedYear;
      if (selectedMonth) params.month = selectedMonth;
      if (selectedAccountType) params.accountType = selectedAccountType;
      if (selectedAccountId) params.account_id = selectedAccountId;

      const response = await dashboardApi.getData(params);
      const data = response.data;

      setSummary(data.summary);
      setDateRanges(data.dateRanges || { years: [], months: [] });
      setAccountIds(data.accountIds || []);
      setBudgetStatus(data.budgetStatus || []);
      setSpendingInsights(data.spendingInsights || []);
      setPendingReview(data.pendingReview || { totalPending: 0, pendingJobs: 0, recentItems: [] });
      setSpendingHabits(data.spendingHabits || null);
      setHistoricalBudgets(data.historicalBudgets || null);
      setHabitsHistory(data.habitsHistory || null);

      if (window.location.pathname === '/dashboard') {
        const newParams = new URLSearchParams();
        if (selectedYear) newParams.set('year', selectedYear);
        if (selectedMonth) newParams.set('month', selectedMonth);
        if (selectedAccountType) newParams.set('accountType', selectedAccountType);
        if (selectedAccountId) newParams.set('accountId', selectedAccountId);
        
        setSearchParams(newParams);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'year') {
      setSelectedYear(value);
      setSelectedMonth('');
    } else if (filterType === 'month') {
      setSelectedMonth(value);
    } else if (filterType === 'accountType') {
      setSelectedAccountType(value);
    } else if (filterType === 'accountId') {
      setSelectedAccountId(value);
    }
  };

  const clearFilters = () => {
    setSelectedMonth('');
    setSelectedAccountType('');
    setSelectedAccountId('');
  };

  const getFilteredMonths = () => {
    if (!selectedYear) return [];
    const yearInt = parseInt(selectedYear);
    const existingMonths = dateRanges.months.filter(month => month.year === yearInt);
    
    // If current year, ensure current month is always available
    const currentDate = new Date();
    if (yearInt === currentDate.getFullYear()) {
      const currentMonthNum = currentDate.getMonth() + 1;
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
      const currentMonthExists = existingMonths.some(m => m.value === currentMonthNum);
      
      if (!currentMonthExists) {
        // Add current month to the list
        const allMonths = [
          ...existingMonths,
          { year: yearInt, value: currentMonthNum, name: monthNames[currentMonthNum - 1] }
        ];
        // Sort by month value descending (most recent first)
        return allMonths.sort((a, b) => b.value - a.value);
      }
    }
    
    return existingMonths;
  };

  // Summary cards data
  const summaryCards = [
    {
      title: 'Total Transactions',
      value: summary?.totalTransactions || 0,
      icon: <ListIcon color="primary" />,
      color: 'primary',
      isNumber: true,
    },
    {
      title: 'Income',
      value: summary?.totalCredits || 0,
      icon: <TrendingUp color="success" />,
      color: 'success',
      isCurrency: true,
    },
    {
      title: 'Spending',
      value: summary?.totalExpenses || 0,
      icon: <TrendingDown color="error" />,
      color: 'error',
      isCurrency: true,
    },
    {
      title: 'Net Balance',
      value: summary?.netBalance || 0,
      icon: <AccountBalance color="info" />,
      color: summary?.netBalance >= 0 ? 'info' : 'warning',
      isCurrency: true,
    },
  ];

  const quickActions = [
    {
      title: 'Upload Statement',
      description: 'Upload and process new bank statements',
      icon: <CloudUpload />,
      color: 'primary',
      path: '/upload',
    },
    {
      title: 'View Transactions',
      description: 'Browse and manage your transactions',
      icon: <ListIcon />,
      color: 'success',
      path: '/transactions',
    },
    {
      title: 'Find Duplicates',
      description: 'Identify and manage duplicate transactions',
      icon: <FindInPage />,
      color: 'warning',
      path: '/duplicates',
    },
    {
      title: 'Settings',
      description: 'Email, categories, accounts and more',
      icon: <Settings />,
      color: 'secondary',
      path: '/settings',
    },
  ];

  const getTrendIcon = (trend) => {
    if (trend === 'increased') return <ArrowUpward sx={{ fontSize: 16, color: 'error.main' }} />;
    if (trend === 'decreased') return <ArrowDownward sx={{ fontSize: 16, color: 'success.main' }} />;
    return <UnchangedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'increased') return 'error';
    if (trend === 'decreased') return 'success';
    return 'default';
  };

  const getHabitIcon = (categoryName) => {
    switch (categoryName) {
      case 'Eating out & delivery':
      case 'Outside Food': return <Restaurant />;
      case 'General shopping':
      case 'Shopping': return <ShoppingBag />;
      case 'Entertainment': return <TheaterComedy />;
      default: return <Analytics />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#2e7d32'; // green
    if (score >= 60) return '#1976d2'; // blue
    if (score >= 40) return '#ed6c02'; // orange
    return '#d32f2f'; // red
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'moderate': return 'warning';
      case 'needs_attention': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Dashboard
        </Typography>
        <LoadingButton
          variant="outlined"
          startIcon={<Refresh />}
          loading={loading}
          onClick={loadDashboardData}
        >
          Refresh
        </LoadingButton>
      </Box>

      <ForwardingSetupCard />

      {/* Filters */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                label="Year"
              >
                {dateRanges.years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => handleFilterChange('month', e.target.value)}
                label="Month"
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                }}
              >
                <MenuItem value="">All Months</MenuItem>
                {getFilteredMonths().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={selectedAccountType}
                onChange={(e) => handleFilterChange('accountType', e.target.value)}
                label="Account Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="bank_account">Bank Account</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Account ID</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => handleFilterChange('accountId', e.target.value)}
                label="Account ID"
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                }}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {accountIds.map((id) => (
                  <MenuItem key={id} value={id}>
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              sx={{ height: '40px' }}
            >
              Clear
            </Button>
            
            <Box sx={{ flexGrow: 1 }} />
            
            <Typography variant="body2" color="text.secondary">
              {selectedMonth && selectedYear
                ? `${getMonthName(parseInt(selectedMonth))} ${selectedYear}`
                : selectedYear
                ? `Year ${selectedYear}`
                : 'All time'}
              {selectedAccountType && ` • ${selectedAccountType === 'bank_account' ? 'Bank' : 'Card'}`}
              {selectedAccountId && ` • ${selectedAccountId}`}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>{card.icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" color={`${card.color}.main`}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {card.isCurrency
                        ? formatCurrency(card.value)
                        : card.isNumber
                        ? card.value.toLocaleString()
                        : card.value}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Money that left your bank accounts, and what it did */}
      <MoneyMovedCards moneyOut={summary?.bankMoneyOut} />

      {/* Budget Status - Full width, positioned prominently */}
      {/* Shows 3 rows when month selected (historicalBudgets), single row otherwise */}
      <Box sx={{ mb: 4 }}>
        <BudgetWidget 
          budgetStatus={budgetStatus} 
          historicalBudgets={historicalBudgets}
          loading={loading} 
        />
      </Box>

      {/* Bills from email: unpaid first, then recently paid (hidden when there are none) */}
      <InsightsCards sx={{ mb: 4 }} />

      <BillsWidget sx={{ mb: 4 }} />

      {/* Recurring payments due soon (hidden when nothing is due) */}
      <UpcomingWidget sx={{ mb: 4 }} />

      {/* Spending Habits Score */}
      {/* Shows 12-month table when month selected (habitsHistory), cards otherwise */}
      {habitsHistory ? (
        <SpendingHabitsTable 
          habitsHistory={habitsHistory}
          currentSpendingHabits={spendingHabits}
          loading={loading}
        />
      ) : spendingHabits && (
        <Card sx={{ mb: 4 }}>
          <CardHeader 
            title="Spending Habits Score" 
            titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
            subheader={spendingHabits.overallMessage}
            avatar={
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={spendingHabits.overallScore}
                  size={56}
                  thickness={4}
                  sx={{ color: getScoreColor(spendingHabits.overallScore) }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ color: getScoreColor(spendingHabits.overallScore) }}>
                    {spendingHabits.overallScore}
                  </Typography>
                </Box>
              </Box>
            }
            action={
              <Chip 
                icon={<EmojiEvents />}
                label={spendingHabits.overallStatus.replace('_', ' ')}
                color={getStatusChipColor(spendingHabits.overallStatus)}
                sx={{ textTransform: 'capitalize' }}
              />
            }
          />
          <CardContent>
            <Grid container spacing={3}>
              {spendingHabits.categories.map((cat) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat.name}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      borderLeft: 4, 
                      borderColor: getScoreColor(cat.score),
                      bgcolor: 'background.default'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ color: getScoreColor(cat.score) }}>
                        {getHabitIcon(cat.name)}
                      </Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {cat.name}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Chip 
                        label={cat.score}
                        size="small"
                        sx={{ 
                          bgcolor: getScoreColor(cat.score),
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 40
                        }}
                      />
                    </Box>
                    
                    <LinearProgress 
                      variant="determinate" 
                      value={cat.score} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        mb: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getScoreColor(cat.score),
                          borderRadius: 3
                        }
                      }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {cat.count} transaction{cat.count !== 1 ? 's' : ''} this period
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Target: ≤{cat.ideal}/mo
                      </Typography>
                    </Box>
                    
                    <Tooltip title={cat.tip} placement="bottom">
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          color: cat.status === 'excellent' ? 'success.main' : 
                                 cat.status === 'needs_attention' ? 'error.main' : 'text.secondary',
                          fontStyle: 'italic',
                          cursor: 'help'
                        }}
                      >
                        {cat.tip.length > 50 ? cat.tip.substring(0, 50) + '...' : cat.tip}
                      </Typography>
                    </Tooltip>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Insights Row: Spending Changes + Pending Review + Category Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Spending Insights */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
            <CardHeader 
              title="Spending Changes" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              subheader={selectedMonth ? "vs previous month" : "vs previous period"}
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate('/analytics')}
                  size="small"
                >
                  Analytics
                </Button>
              }
              sx={{ 
                pb: 1,
                '& .MuiCardHeader-action': {
                  mt: 0.5,
                  ml: 2
                }
              }}
            />
            <CardContent sx={{ 
              flexGrow: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              p: 0
            }}>
              {spendingInsights.length > 0 ? (
                <List sx={{ 
                  overflowY: 'auto',
                  flexGrow: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                  },
                }}>
                  {spendingInsights.map((insight, index) => (
                    <React.Fragment key={insight.category}>
                      <ListItem sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getTrendIcon(insight.trend)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" fontWeight={500}>
                                {insight.category}
                              </Typography>
                              <Chip
                                label={`${insight.percentChange > 0 ? '+' : ''}${insight.percentChange}%`}
                                size="small"
                                color={getTrendColor(insight.trend)}
                                variant="outlined"
                                sx={{ ml: 1, minWidth: 60 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Tooltip title={`Previous: ${formatCurrency(insight.previousAmount)}`}>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(insight.currentAmount)}
                                {insight.change !== 0 && (
                                  <span style={{ color: insight.change > 0 ? '#d32f2f' : '#2e7d32' }}>
                                    {' '}({insight.change > 0 ? '+' : ''}{formatCurrency(insight.change)})
                                  </span>
                                )}
                              </Typography>
                            </Tooltip>
                          }
                        />
                      </ListItem>
                      {index < spendingInsights.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Analytics sx={{ fontSize: 48, color: 'text.secondary' }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    No spending data to compare
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select a month to see changes vs previous month
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Review */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
            <CardHeader 
              title="Pending Review" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                pendingReview.totalPending > 0 ? (
                  <Chip 
                    label={`${pendingReview.totalPending} items`}
                    color="warning"
                    size="small"
                  />
                ) : null
              }
              sx={{ 
                pb: 1,
                '& .MuiCardHeader-action': {
                  mt: 0.5,
                  ml: 2
                }
              }}
            />
            <CardContent sx={{ 
              flexGrow: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              p: 0
            }}>
              {pendingReview.totalPending > 0 || pendingReview.pendingJobs > 0 ? (
                <>
                  {/* Summary Stats */}
                  <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {pendingReview.pendingJobs > 0 && (
                        <Chip 
                          icon={<Schedule sx={{ fontSize: 16 }} />}
                          label={`${pendingReview.pendingJobs} jobs processing`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {pendingReview.recentItems.length > 0 ? (
                    <List sx={{ 
                      overflowY: 'auto',
                      flexGrow: 1,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#888',
                        borderRadius: '4px',
                      },
                    }}>
                      {pendingReview.recentItems.map((item, index) => (
                        <React.Fragment key={item.id}>
                          <ListItem 
                            sx={{ py: 1, cursor: 'pointer' }}
                            onClick={() => navigate(item.jobType === 'email' ? '/review' : '/processing-jobs')}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {item.description}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={item.type}
                                    size="small"
                                    color={item.type === 'credit' ? 'success' : 'error'}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {item.category || 'Uncategorized'}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={item.type === 'credit' ? 'success.main' : 'error.main'}
                            >
                              {item.type === 'credit' ? '+' : '-'}{formatCurrency(item.amount)}
                            </Typography>
                          </ListItem>
                          {index < pendingReview.recentItems.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Jobs are processing...
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="warning"
                      startIcon={<RateReview />}
                      // Emails are reviewed together; uploaded files job by job
                      onClick={() => navigate(pendingReview.recentItems.some(i => i.jobType === 'pdf') ? '/processing-jobs' : '/review')}
                    >
                      Review All
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <RateReview sx={{ fontSize: 48, color: 'success.main' }} />
                  <Typography variant="body1" color="success.main" sx={{ mt: 2, fontWeight: 500 }}>
                    All caught up!
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No transactions pending review
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Breakdown */}
        <Grid size={{ xs: 12, md: 12, lg: 4 }}>
          <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
            <CardHeader 
              title="Expense Categories" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate('/transactions')}
                  size="small"
                >
                  View All
                </Button>
              }
              sx={{ 
                pb: 1,
                '& .MuiCardHeader-action': {
                  mt: 0.5,
                  ml: 2
                }
              }}
            />
            <CardContent sx={{ 
              flexGrow: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              p: 0
            }}>
              {summary?.categoryBreakdown && Object.keys(summary.categoryBreakdown).length > 0 ? (
                <List sx={{ 
                  overflowY: 'auto',
                  flexGrow: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                  },
                }}>
                  {Object.entries(summary.categoryBreakdown)
                    .sort(([,a], [,b]) => b - a)
                    .map(([category, amount]) => (
                      <ListItem key={category}>
                        <ListItemText
                          primary={category}
                          secondary={formatCurrency(amount)}
                        />
                        <Chip
                          label={`${((amount / summary.totalExpenses) * 100).toFixed(1)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </ListItem>
                    ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Analytics sx={{ fontSize: 48, color: 'text.secondary' }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    No expense data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card>
        <CardHeader title="Quick Actions" />
        <CardContent>
          <Grid container spacing={3}>
            {quickActions.map((action, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => navigate(action.path)}
                >
                  <Box sx={{ color: `${action.color}.main`, mb: 2 }}>
                    {React.cloneElement(action.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
